// src/pages/TheoDoiVoCanPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import {
  collection,
  onSnapshot,
  query,
  orderBy as fbOrderBy,
  doc,
  updateDoc,
  serverTimestamp,
  runTransaction,
  addDoc,
  Timestamp,
} from 'firebase/firestore';

import {
  Box,
  Typography,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  TablePagination,
  Chip,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
  Autocomplete,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Divider,
} from '@mui/material';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';

import { startOfMonth, endOfMonth, startOfDay, endOfDay } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';

// ===== Helpers =====
const formatDateTime = (ts) => {
  if (!ts) return 'N/A';
  try {
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts.seconds * 1000);
    return d.toLocaleString('vi-VN');
  } catch {
    return 'N/A';
  }
};

const formatNumber = (n) => {
  if (typeof n !== 'number') return 0;
  return new Intl.NumberFormat('vi-VN').format(n);
};

const getStatusChip = (status) => {
  switch (status) {
    case 'shipped':
      return <Chip label="Đã chuyển" color="success" size="small" />;
    case 'cancelled':
      return <Chip label="Hàng Hủy" color="error" size="small" />;
    case 'other':
      return <Chip label="Khác" color="default" size="small" />;
    default:
      return <Chip label={status || 'Không rõ'} color="info" size="small" />;
  }
};

// Sum quantities of items displayed on each row
const sumQuantity = (rows) =>
  rows.reduce((acc, r) => {
    const items = Array.isArray(r.displayItems) ? r.displayItems : r.recoveredItems || [];
    const rowSum = items.reduce((s, it) => s + Number(it.quantity || 0), 0);
    return acc + rowSum;
  }, 0);

// ====== Tạo mã TO tự động: "TOyyyymmddhhmmss" ======
const pad2 = (n) => String(n).padStart(2, '0');
const generateTO = (d = new Date()) => {
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const mi = pad2(d.getMinutes());
  const ss = pad2(d.getSeconds());
  return `TO${yyyy}${mm}${dd}${hh}${mi}${ss}`;
};

function TheoDoiVoCanPage() {
  const [recoveries, setRecoveries] = useState([]);
  const [exports, setExports] = useState([]);
  const [products, setProducts] = useState([]);
  const [drivers, setDrivers] = useState([]); // danh sách tài xế
  const [loading, setLoading] = useState(true);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { currentUser } = useAuth();

  // Filters & edit states
  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(endOfMonth(new Date()));
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState(null); // string | null
  const [productFilter, setProductFilter] = useState(''); // string
  const [soFilter, setSoFilter] = useState(''); // lọc theo Mã SO/TO

  const [editingRowId, setEditingRowId] = useState(null);
  const [editedItems, setEditedItems] = useState([]);

  // ===== Tạo mới (Dialog) =====
  const [openCreate, setOpenCreate] = useState(false);
  const [createDate, setCreateDate] = useState(null);
  const [createTO, setCreateTO] = useState(''); // <— thay cho SO
  const [createCustomer, setCreateCustomer] = useState('');
  const [createStatus, setCreateStatus] = useState('other');
  const [createItems, setCreateItems] = useState([
    { productId: '', productName: '', productSku: '', quantity: 1 },
  ]);

  // các trường bổ sung
  const [createDriverName, setCreateDriverName] = useState(''); // Tên tài xế (chọn từ drivers)
  const [createAssistantName, setCreateAssistantName] = useState(''); // Phụ xe (chọn từ drivers)
  const [createNote, setCreateNote] = useState(''); // Ghi chú

  // ===== Subscriptions =====
  useEffect(() => {
    const qRecoveries = query(collection(db, 'recoveries'), fbOrderBy('createdAt', 'desc'));
    const unsubRecoveries = onSnapshot(qRecoveries, (snapshot) => {
      setRecoveries(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    const qExports = query(collection(db, 'exports'));
    const unsubExports = onSnapshot(qExports, (snapshot) => {
      setExports(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    const qProducts = query(collection(db, 'products'));
    const unsubProducts = onSnapshot(qProducts, (snapshot) => {
      setProducts(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    // subscribe danh sách drivers
    const qDrivers = query(collection(db, 'drivers'), fbOrderBy('name', 'asc'));
    const unsubDrivers = onSnapshot(qDrivers, (snapshot) => {
      setDrivers(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return () => {
      unsubRecoveries();
      unsubExports();
      unsubProducts();
      unsubDrivers();
    };
  }, []);

  // Danh sách duy nhất cho các bộ lọc/gợi ý
  const uniqueCustomers = useMemo(() => {
    const names = new Set();
    for (const exp of exports) if (exp?.customerName) names.add(exp.customerName);
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'vi'));
  }, [exports]);

  const uniqueProductNames = useMemo(() => {
    const names = new Set();
    for (const p of products) if (p?.name) names.add(p.name);
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'vi'));
  }, [products]);

  const uniqueSoNumbers = useMemo(() => {
    const set = new Set();
    for (const r of recoveries) if (r?.soNumber) set.add(r.soNumber);
    return Array.from(set).sort();
  }, [recoveries]);

  const driverNames = useMemo(() => {
    const set = new Set();
    for (const d of drivers) {
      const name = d?.name || d?.fullName || d?.displayName || d?.driverName || '';
      if (name) set.add(name);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'vi'));
  }, [drivers]);

  // Khi thay đổi các filter -> về trang đầu
  useEffect(() => {
    setPage(0);
  }, [startDate, endDate, statusFilter, customerFilter, productFilter, soFilter]);

  // Map SO -> customer (để tương thích dữ liệu cũ; TO mới không cần map này)
  const exportMap = useMemo(
    () => new Map(exports.map((exp) => [exp.soNumber, exp.customerName])),
    [exports]
  );

  // Base filtered (lọc theo ngày + KH + Tên SP + SO/TO, loại bỏ processing)
  const baseFiltered = useMemo(() => {
    const start = startDate ? startOfDay(startDate) : null;
    const end = endDate ? endOfDay(endDate) : null;
    const term = (productFilter || '').trim().toLowerCase();
    const soTerm = (soFilter || '').trim().toLowerCase();

    return recoveries
      .map((it) => {
        const customerName = exportMap.get(it.soNumber) || it.customerName || 'Không rõ';
        let displayItems = Array.isArray(it.recoveredItems) ? it.recoveredItems : [];
        if (term) {
          displayItems = displayItems.filter((x) =>
            (x.productName || '').toLowerCase().includes(term)
          );
        }
        return { ...it, customerName, displayItems };
      })
      .filter((it) => {
        const hasValidItems =
          Array.isArray(it.displayItems) &&
          it.displayItems.length > 0 &&
          it.displayItems.some((prod) => prod.productName);
        if (!hasValidItems) return false;

        const itemDate = it.createdAt?.toDate?.() || null;
        if (!itemDate) return false;
        const dateMatch = start && end ? itemDate >= start && itemDate <= end : true;

        const st = it.statusUpdatedTo || 'processing';
        const isNotProcessing = st !== 'processing';

        const customerMatch =
          !customerFilter ||
          (it.customerName || '').toLowerCase() === customerFilter.toLowerCase();

        const soMatch =
          !soTerm || String(it.soNumber || '').toLowerCase().includes(soTerm);

        return dateMatch && isNotProcessing && customerMatch && soMatch;
      });
  }, [recoveries, exportMap, startDate, endDate, customerFilter, productFilter, soFilter]);

  // Totals theo trạng thái
  const totalsByStatus = useMemo(() => {
    const buckets = { all: 0, shipped: 0, cancelled: 0, other: 0 };
    for (const r of baseFiltered) {
      const st = r.statusUpdatedTo || 'other';
      const items = Array.isArray(r.displayItems) ? r.displayItems : r.recoveredItems || [];
      const qty = items.reduce((s, it) => s + Number(it.quantity || 0), 0);
      buckets.all += qty;
      if (st === 'shipped') buckets.shipped += qty;
      else if (st === 'cancelled') buckets.cancelled += qty;
      else buckets.other += qty;
    }
    return buckets;
  }, [baseFiltered]);

  // processedRecoveries sau khi lọc theo trạng thái
  const processedRecoveries = useMemo(() => {
    if (statusFilter === 'all') return baseFiltered;
    return baseFiltered.filter((it) => (it.statusUpdatedTo || 'other') === statusFilter);
  }, [baseFiltered, statusFilter]);

  // Tổng SL và Tổng số đơn (SO/TO) trên dữ liệu đang lọc
  const processedTotalQty = useMemo(() => sumQuantity(processedRecoveries), [processedRecoveries]);

  // >>> NEW: Tổng số Đơn (đếm soNumber duy nhất) <<<
  const processedTotalOrders = useMemo(
    () => new Set(processedRecoveries.map((r) => r.soNumber).filter(Boolean)).size,
    [processedRecoveries]
  );

  // ===== Table handlers =====
  const handleChangePage = (e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // ===== Edit handlers =====
  const handleEdit = (row) => {
    setEditingRowId(row.id);
    const items = Array.isArray(row.displayItems) ? row.displayItems : row.recoveredItems || [];
    setEditedItems(JSON.parse(JSON.stringify(items)));
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
    setEditedItems([]);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...editedItems];
    if (field === 'product') {
      if (!value) {
        newItems[index] = { ...newItems[index], productId: '', productName: '', productSku: '' };
      } else {
        newItems[index] = {
          ...newItems[index],
          productId: value.id,
          productName: value.name,
          productSku: value.sku,
        };
      }
    } else if (field === 'quantity') {
      const num = parseInt(value, 10);
      newItems[index].quantity = Number.isNaN(num) ? 0 : num;
    }
    setEditedItems(newItems);
  };

  const handleDeleteItem = (idx) => {
    setEditedItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAddItem = () => {
    setEditedItems((prev) => [
      ...prev,
      { productId: '', productName: '', productSku: '', quantity: 1 },
    ]);
  };

  const handleSaveEdit = async (row) => {
    const valid = editedItems.filter((it) => it.productId && it.productName && it.quantity > 0);
    if (valid.length === 0) {
      alert('Vui lòng thêm ít nhất một sản phẩm hợp lệ.');
      return;
    }
    try {
      await updateDoc(doc(db, 'recoveries', row.id), { recoveredItems: valid });
      alert('Cập nhật thành công!');
      handleCancelEdit();
    } catch (err) {
      console.error(err);
      alert('Có lỗi xảy ra khi cập nhật.');
    }
  };

  // ===== Approve =====
  const handleApprove = async (recoveryItem) => {
    const items = Array.isArray(recoveryItem.displayItems)
      ? recoveryItem.displayItems
      : recoveryItem.recoveredItems || [];
    if (!recoveryItem || items.length === 0) {
      return alert('Không có sản phẩm nào để duyệt.');
    }
    if (!window.confirm('Bạn có chắc muốn duyệt? Hành động này sẽ cộng số lượng vào tồn kho.')) return;

    try {
      await runTransaction(db, async (transaction) => {
        const recoveryRef = doc(db, 'recoveries', recoveryItem.id);

        const recoverySnap = await transaction.get(recoveryRef);
        if (!recoverySnap.exists()) throw new Error('Phiếu thu hồi không tồn tại.');
        if (recoverySnap.data()?.approvedBy) throw new Error('Phiếu này đã được duyệt trước đó.');

        const productRefs = items.map((it) => doc(db, 'products', it.productId));
        const productSnaps = await Promise.all(productRefs.map((r) => transaction.get(r)));

        const updates = items.map((it, idx) => {
          const snap = productSnaps[idx];
          if (!snap.exists()) {
            throw new Error(`Sản phẩm ${it.productName || it.productId} không tồn tại.`);
          }
          const currentQty = Number(snap.data().quantity || 0);
          const addQty = Number(it.quantity || 0);
          return { ref: productRefs[idx], newQuantity: currentQty + addQty };
        });

        for (const u of updates) transaction.update(u.ref, { quantity: u.newQuantity });

        transaction.update(recoveryRef, {
          approvedBy: currentUser?.email || 'system',
          approvedAt: serverTimestamp(),
        });
      });

      alert('Duyệt và cập nhật tồn kho thành công!');
    } catch (error) {
      console.error('Lỗi khi duyệt:', error);
      alert(`Đã có lỗi xảy ra: ${error.message}`);
    }
  };

  // ===== Export to Excel =====
  const handleExportExcel = () => {
    if (processedRecoveries.length === 0) {
      alert('Không có dữ liệu để xuất.');
      return;
    }
    const dataToExport = processedRecoveries.flatMap((item) => {
      const items = Array.isArray(item.displayItems) ? item.displayItems : item.recoveredItems || [];
      return items.map((prod) => ({
        'Ngày tạo': formatDateTime(item.createdAt),
        'Mã SO/TO': item.soNumber, // field soNumber chứa TO
        'Khách hàng': item.customerName,
        'Tên tài xế': item.driverName || '',
        'Phụ xe': item.assistantName || '',
        'Ghi chú': item.note || '',
        'Tên sản phẩm': prod.productName,
        'Số lượng': prod.quantity,
        'Trạng thái': getStatusChip(item.statusUpdatedTo).props.label,
        'Người duyệt': item.approvedBy || 'Chưa duyệt',
      }));
    });

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'TheoDoiVoCan');
    XLSX.writeFile(
      wb,
      `TheoDoiVoCan_${new Date().toLocaleDateString('vi-VN').replace(/\//g, '-')}.xlsx`
    );
  };

  // ===== Create (open/close/reset) =====
  const resetCreateForm = () => {
    setCreateDate(null);
    setCreateTO(''); // sẽ set ngay khi mở dialog
    setCreateCustomer('');
    setCreateStatus('other');
    setCreateItems([{ productId: '', productName: '', productSku: '', quantity: 1 }]);
    setCreateDriverName('');
    setCreateAssistantName('');
    setCreateNote('');
  };

  const handleOpenCreate = () => {
    resetCreateForm();
    setCreateTO(generateTO()); // tạo TO khi mở form
    setOpenCreate(true);
  };

  const handleCloseCreate = () => {
    setOpenCreate(false);
  };

  // Thay đổi item trong form tạo mới
  const handleCreateItemChange = (idx, field, value) => {
    const newItems = [...createItems];
    if (field === 'product') {
      if (!value) {
        newItems[idx] = { productId: '', productName: '', productSku: '', quantity: 1 };
      } else {
        newItems[idx] = {
          ...newItems[idx],
          productId: value.id,
          productName: value.name,
          productSku: value.sku,
        };
      }
    } else if (field === 'quantity') {
      const num = parseInt(value, 10);
      newItems[idx].quantity = Number.isNaN(num) ? 0 : num;
    }
    setCreateItems(newItems);
  };

  const handleCreateAddRow = () => {
    setCreateItems((prev) => [...prev, { productId: '', productName: '', productSku: '', quantity: 1 }]);
  };

  const handleCreateDeleteRow = (idx) => {
    setCreateItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmitCreate = async () => {
    const validItems = createItems.filter((it) => it.productId && it.productName && it.quantity > 0);
    if (!createTO) return alert('Mã TO chưa được tạo.');
    if (!createCustomer) return alert('Vui lòng nhập/chọn Khách hàng.');
    if (validItems.length === 0) return alert('Vui lòng thêm ít nhất một sản phẩm hợp lệ.');

    try {
      const payload = {
        soNumber: createTO, // <— chứa TOyyyymmddhhmmss
        customerName: createCustomer,
        statusUpdatedTo: createStatus || 'other',
        recoveredItems: validItems,
        createdBy: currentUser?.email || 'system',
        createdAt: createDate ? Timestamp.fromDate(createDate) : serverTimestamp(),
        driverName: createDriverName || '',
        assistantName: createAssistantName || '',
        note: createNote || '',
      };
      await addDoc(collection(db, 'recoveries'), payload);
      alert('Thêm phiếu thu hồi thành công!');
      handleCloseCreate();
    } catch (e) {
      console.error(e);
      alert('Có lỗi khi tạo phiếu thu hồi.');
    }
  };

  const canApprove =
    currentUser?.roleNames?.some((n) => n.toLowerCase() === 'admin') ||
    currentUser?.permissions?.full_access ||
    currentUser?.permissions?.approveCanRecovery;

  const canEdit =
    currentUser?.roleNames?.some((n) => n.toLowerCase() === 'admin') ||
    currentUser?.permissions?.full_access ||
    currentUser?.permissions?.editExports;

  const canCreate =
    currentUser?.roleNames?.some((n) => n.toLowerCase() === 'admin') ||
    currentUser?.permissions?.full_access ||
    currentUser?.permissions?.createCanRecovery ||
    true;

  if (loading) return <Typography>Đang tải dữ liệu...</Typography>;

  const labelAll = `Tất cả (SL: ${formatNumber(totalsByStatus.all)})`;
  const labelShipped = `Đã chuyển (SL: ${formatNumber(totalsByStatus.shipped)})`;
  const labelCancelled = `Hàng hủy (SL: ${formatNumber(totalsByStatus.cancelled)})`;
  const labelOther = `Khác (SL: ${formatNumber(totalsByStatus.other)})`;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Theo Dõi Thu Hồi Vỏ Can
      </Typography>

      {/* Bộ lọc + Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Từ ngày"
                value={startDate}
                onChange={(v) => setStartDate(v)}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Đến ngày"
                value={endDate}
                onChange={(v) => setEndDate(v)}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>

            {/* Lọc theo Khách hàng */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={uniqueCustomers}
                value={customerFilter}
                onChange={(e, v) => setCustomerFilter(v)}
                size="small"
                fullWidth
                clearOnEscape
                sx={{
                  minWidth: { xs: '100%', md: 560 },
                  '& .MuiInputBase-root': { height: 44 },
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Lọc theo Khách hàng" placeholder="Chọn khách hàng" />
                )}
              />
            </Grid>

            {/* Lọc theo Tên sản phẩm */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={uniqueProductNames}
                freeSolo
                value={productFilter}
                onChange={(e, v) => setProductFilter(v || '')}
                onInputChange={(e, v) => setProductFilter(v || '')}
                size="small"
                fullWidth
                clearOnEscape
                sx={{
                  minWidth: { xs: '100%', md: 480 },
                  '& .MuiInputBase-root': { height: 44 },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Lọc theo Tên sản phẩm"
                    placeholder="Nhập hoặc chọn tên sản phẩm"
                  />
                )}
              />
            </Grid>

            {/* Lọc theo Mã SO/TO */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={uniqueSoNumbers}
                freeSolo
                value={soFilter}
                onChange={(e, v) => setSoFilter(v || '')}
                onInputChange={(e, v) => setSoFilter(v || '')}
                size="small"
                fullWidth
                clearOnEscape
                sx={{
                  minWidth: { xs: '100%', md: 360 },
                  '& .MuiInputBase-root': { height: 44 },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Lọc theo Mã SO/TO"
                    placeholder="Nhập hoặc chọn Mã SO/TO"
                  />
                )}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
            >
              <ToggleButtonGroup
                value={statusFilter}
                exclusive
                onChange={(e, v) => v && setStatusFilter(v)}
                size="small"
                sx={{ flexWrap: 'wrap' }}
              >
                <ToggleButton value="all">{labelAll}</ToggleButton>
                <ToggleButton value="shipped">{labelShipped}</ToggleButton>
                <ToggleButton value="cancelled">{labelCancelled}</ToggleButton>
                <ToggleButton value="other">{labelOther}</ToggleButton>
              </ToggleButtonGroup>

              {/* >>> NEW: Chip hiển thị Tổng số Đơn (SO/TO) đang lọc <<< */}
              <Chip
                color="secondary"
                variant="outlined"
                label={`Số đơn (SO/TO): ${formatNumber(processedTotalOrders)}`}
              />

              <Chip
                color="primary"
                variant="outlined"
                label={`Tổng SL (đang lọc): ${formatNumber(processedTotalQty)}`}
              />
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportExcel}>
                Xuất Excel
              </Button>
              {canCreate && (
                <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={handleOpenCreate}>
                  Nhập liệu
                </Button>
              )}
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Paper>

      {/* Bảng chính */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 140 }}>Ngày tạo</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 160 }}>Mã SO/TO</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 220 }}>Khách hàng</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 150 }}>Tài xế</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 150 }}>Phụ xe</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 240 }}>Ghi chú</TableCell>
                <TableCell sx={{ minWidth: 240 }}>Tên sản phẩm</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 120 }}>Số lượng</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 140 }}>Trạng thái</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 160 }}>Người duyệt</TableCell>
                {canApprove && <TableCell align="center" sx={{ minWidth: 160 }}>Thao tác</TableCell>}
              </TableRow>
            </TableHead>

            <TableBody>
              {processedRecoveries
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  const items = Array.isArray(row.displayItems)
                    ? row.displayItems
                    : row.recoveredItems || [];
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.soNumber}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.customerName}</TableCell>

                      {/* Hiển thị Tài xế / Phụ xe / Ghi chú */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.driverName || '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.assistantName || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }} title={row.note || ''}>
                          {row.note || '-'}
                        </Typography>
                      </TableCell>

                      {/* Tên SP */}
                      <TableCell sx={{ p: 1, verticalAlign: 'top' }}>
                        {(editingRowId === row.id ? editedItems : items).map((prod, idx, arr) => (
                          <Box
                            key={idx}
                            sx={{
                              py: 1,
                              borderBottom: idx < arr.length - 1 ? '1px solid #eee' : 'none',
                              minHeight: '52px',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {editingRowId === row.id ? (
                              <Autocomplete
                                fullWidth
                                size="small"
                                options={products}
                                getOptionLabel={(opt) => opt.name || ''}
                                value={products.find((p) => p.id === prod.productId) || null}
                                onChange={(e, v) => handleItemChange(idx, 'product', v)}
                                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                renderInput={(params) => (
                                  <TextField {...params} variant="standard" placeholder="Chọn sản phẩm..." />
                                )}
                              />
                            ) : (
                              <Typography variant="body2" noWrap title={prod.productName}>
                                {prod.productName}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </TableCell>

                      {/* Số lượng */}
                      <TableCell align="right" sx={{ p: 1, verticalAlign: 'top' }}>
                        {(editingRowId === row.id ? editedItems : items).map((prod, idx, arr) => (
                          <Box
                            key={idx}
                            sx={{
                              py: 1,
                              borderBottom: idx < arr.length - 1 ? '1px solid #eee' : 'none',
                              minHeight: '52px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                            }}
                          >
                            {editingRowId === row.id ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  variant="standard"
                                  value={prod.quantity || ''}
                                  onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                                  sx={{ input: { textAlign: 'right' }, width: '80px' }}
                                />
                                <IconButton size="small" onClick={() => handleDeleteItem(idx)} color="error" sx={{ ml: 0.5 }}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <Typography variant="body2">{formatNumber(prod.quantity)}</Typography>
                            )}
                          </Box>
                        ))}
                      </TableCell>

                      <TableCell>{getStatusChip(row.statusUpdatedTo)}</TableCell>
                      <TableCell>{row.approvedBy || 'Chưa duyệt'}</TableCell>

                      {canApprove && (
                        <TableCell align="center">
                          {editingRowId === row.id ? (
                            <Box>
                              <Tooltip title="Thêm dòng">
                                <IconButton size="small" onClick={handleAddItem} color="info">
                                  <AddCircleOutlineIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Lưu thay đổi">
                                <IconButton size="small" onClick={() => handleSaveEdit(row)} color="primary">
                                  <SaveIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Hủy">
                                <IconButton size="small" onClick={handleCancelEdit} color="error">
                                  <CancelIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <>
                              {canEdit && (
                                <Tooltip title="Chỉnh sửa">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEdit(row)}
                                      disabled={!!row.approvedBy}
                                      color="warning"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                              <Tooltip title="Duyệt và cộng vào kho">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleApprove(row)}
                                    disabled={!!row.approvedBy}
                                    color="success"
                                  >
                                    <CheckCircleOutlineIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={processedRecoveries.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Số dòng mỗi trang:"
        />
      </Paper>

      {/* ===== Dialog Nhập liệu ===== */}
      <Dialog open={openCreate} onClose={handleCloseCreate} maxWidth="xl" fullWidth>
        <DialogTitle>Nhập liệu Thu hồi vỏ can</DialogTitle>
        <DialogContent dividers>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {/* Hàng 1: Ngày tạo (tùy chọn) & Trạng thái */}
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Ngày tạo (tùy chọn)"
                  value={createDate}
                  onChange={(v) => setCreateDate(v)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="status-label">Trạng thái</InputLabel>
                  <Select
                    labelId="status-label"
                    label="Trạng thái"
                    value={createStatus}
                    onChange={(e) => setCreateStatus(e.target.value)}
                  >
                    <MenuItem value="shipped">Đã chuyển</MenuItem>
                    <MenuItem value="cancelled">Hàng hủy</MenuItem>
                    <MenuItem value="other">Khác</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Hàng 2: Mã TO tự tạo (readonly) */}
              <Grid item xs={12}>
                <TextField
                  label="Mã TO (tự tạo)"
                  value={createTO}
                  fullWidth
                  size="small"
                  InputProps={{ readOnly: true, sx: { height: 48 } }}
                  helperText="Định dạng: TOyyyymmddhhmmss (tự sinh khi mở form)"
                />
              </Grid>

              {/* Hàng 3: Khách hàng */}
              <Grid item xs={12}>
                <Autocomplete
                  options={uniqueCustomers}
                  freeSolo
                  value={createCustomer}
                  onChange={(e, v) => setCreateCustomer(v || '')}
                  onInputChange={(e, v) => setCreateCustomer(v || '')}
                  size="small"
                  fullWidth
                  sx={{
                    minWidth: { xs: '100%', md: 900 },
                    '& .MuiInputBase-root': { height: 48 },
                    '& input': { fontSize: 16 },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Khách hàng"
                      placeholder="Nhập hoặc chọn KH"
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {/* Hàng 4 — Tên tài xế & Phụ xe (từ drivers) */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={driverNames}
                  value={createDriverName}
                  onChange={(e, v) => setCreateDriverName(v || '')}
                  size="small"
                  fullWidth
                  disableClearable={false}
                  sx={{ '& .MuiInputBase-root': { height: 48 } }}
                  renderInput={(params) => (
                    <TextField {...params} label="Tên tài xế" placeholder="Chọn tài xế" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={driverNames}
                  value={createAssistantName}
                  onChange={(e, v) => setCreateAssistantName(v || '')}
                  size="small"
                  fullWidth
                  disableClearable={false}
                  sx={{ '& .MuiInputBase-root': { height: 48 } }}
                  renderInput={(params) => (
                    <TextField {...params} label="Phụ xe" placeholder="Chọn phụ xe" />
                  )}
                />
              </Grid>

              {/* Hàng 5 — Ghi chú */}
              <Grid item xs={12}>
                <TextField
                  label="Ghi chú"
                  value={createNote}
                  onChange={(e) => setCreateNote(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Nhập ghi chú (nếu có)"
                  multiline
                  minRows={2}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>

          <Divider sx={{ my: 2 }} />

          {/* Bảng nhập sản phẩm */}
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Danh sách sản phẩm
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="60%">Sản phẩm</TableCell>
                <TableCell align="right" width="25%">Số lượng</TableCell>
                <TableCell align="center" width="15%">Xóa</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {createItems.map((it, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Autocomplete
                      options={products}
                      size="small"
                      getOptionLabel={(opt) => opt.name || ''}
                      value={products.find((p) => p.id === it.productId) || null}
                      isOptionEqualToValue={(opt, val) => opt.id === val.id}
                      onChange={(e, v) => handleCreateItemChange(idx, 'product', v)}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Chọn sản phẩm..." variant="outlined" />
                      )}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      value={it.quantity}
                      onChange={(e) => handleCreateItemChange(idx, 'quantity', e.target.value)}
                      inputProps={{ min: 0, style: { textAlign: 'right' } }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton color="error" onClick={() => handleCreateDeleteRow(idx)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={3} align="left">
                  <Button onClick={handleCreateAddRow} startIcon={<AddCircleOutlineIcon />}>
                    Thêm dòng
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseCreate} startIcon={<CancelIcon />}>
            Hủy
          </Button>
          <Button variant="contained" onClick={handleSubmitCreate} startIcon={<SaveIcon />}>
            Lưu phiếu
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TheoDoiVoCanPage;
